import React from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { Text } from './Text';
import { radius, useTheme } from './theme';

export interface TabItem<K extends string = string> {
  key: K;
  label: string;
  count?: number;
}

/** Underlined tabs (horizontally scrollable). */
export function Tabs<K extends string>({ tabs, value, onChange }: { tabs: TabItem<K>[]; value: K; onChange: (k: K) => void }) {
  const { colors } = useTheme();
  return (
    <View style={{ borderBottomWidth: 1, borderBottomColor: colors.border }}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 4 }}>
        {tabs.map((t) => {
          const sel = t.key === value;
          return (
            <Pressable key={t.key} accessibilityRole="tab" accessibilityState={{ selected: sel }} onPress={() => onChange(t.key)} style={{ paddingHorizontal: 12, paddingVertical: 10, borderBottomWidth: 2, borderBottomColor: sel ? colors.primary : 'transparent', marginBottom: -1 }}>
              <Text weight={sel ? 'semibold' : 'medium'} color={sel ? colors.primary : colors.muted}>
                {t.label}
                {t.count != null ? ` (${t.count})` : ''}
              </Text>
            </Pressable>
          );
        })}
      </ScrollView>
    </View>
  );
}

/** Pill-style segmented control. */
export function Segmented<K extends string>({ options, value, onChange, block }: { options: TabItem<K>[]; value: K; onChange: (k: K) => void; block?: boolean }) {
  const { colors } = useTheme();
  return (
    <View style={{ flexDirection: 'row', backgroundColor: colors.surface2, borderWidth: 1, borderColor: colors.border, borderRadius: radius.md, padding: 3, gap: 2, alignSelf: block ? 'stretch' : 'flex-start' }}>
      {options.map((o) => {
        const sel = o.key === value;
        return (
          <Pressable key={o.key} accessibilityRole="button" accessibilityState={{ selected: sel }} onPress={() => onChange(o.key)} style={{ flex: block ? 1 : undefined, paddingHorizontal: 12, paddingVertical: 6, borderRadius: radius.sm, backgroundColor: sel ? colors.surface : 'transparent', alignItems: 'center', borderWidth: sel ? 1 : 0, borderColor: colors.border }}>
            <Text size={13} weight={sel ? 'semibold' : 'medium'} color={sel ? colors.text : colors.muted}>
              {o.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

/** Wizard steps indicator. */
export function Stepper({ steps, current, onStepPress }: { steps: string[]; current: number; onStepPress?: (i: number) => void }) {
  const { colors } = useTheme();
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
      {steps.map((s, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <React.Fragment key={s}>
            <Pressable disabled={!onStepPress} onPress={() => onStepPress?.(i)} style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <View style={{ width: 24, height: 24, borderRadius: 12, alignItems: 'center', justifyContent: 'center', backgroundColor: done ? colors.success : active ? colors.primary : colors.neutralSoft }}>
                <Text size={12} weight="bold" color={done || active ? '#fff' : colors.muted}>
                  {done ? '✓' : i + 1}
                </Text>
              </View>
              <Text weight={active ? 'semibold' : 'regular'} color={active ? colors.text : colors.muted}>
                {s}
              </Text>
            </Pressable>
            {i < steps.length - 1 && <View style={{ width: 24, height: 1, backgroundColor: colors.borderStrong }} />}
          </React.Fragment>
        );
      })}
    </View>
  );
}
